import { Idol } from 'types';
// Import both mods and modDatabase to accommodate different import structures
import { mods, modDatabase } from '../data/mods';

interface TradeFilter {
  id: string;
  value?: {
    min: number;
  };
  disabled?: boolean;
}

interface TradeQuery {
  query: {
    name?: string;
    // type field removed
    stats: Array<{
      type: string;
      filters: TradeFilter[];
    }>;
    status: {
      option: string;
    };
  };
}

/**
 * Looks up a mod's tradeId from the mod database using the modId
 * @param {number} modId - The mod ID to look up
 * @returns {string|null} - The trade ID or null if not found
 */
export function getTradeIdFromModId(modId: number): string | null {
  if (!modId) return null;
  
  // Handle both numeric and string keys in the mod database
  let mod;
  
  // Try different ways to access the mod
  // 1. Direct access with modId
  mod = modDatabase?.mods?.[modId];
  
  // 2. Try string conversion if direct access failed
  if (!mod) {
    mod = modDatabase?.mods?.[String(modId)];
  }
  
  // 3. Try from mods object directly
  if (!mod) {
    // Search in the mods object for matching id
    const foundMod = Object.values(mods || {}).find(m => m.id === modId);
    if (foundMod) {
      mod = foundMod;
    }
  }
  
  return mod?.tradeId || null;
}

/**
 * Generates a Path of Exile trade URL for an idol based on its stats
 * @param {Idol} idol - The idol object with stats
 * @returns {string} - The formatted trade URL
 */
export const generateTradeUrl = (idol: Idol): string => {
  if (!idol || !idol.stats) return "";

  // Hard-coded idol type or use the size name
  const league = "Phrecia 2.0"; // Current league name
  
  // Create filters from stats
  const filters: TradeFilter[] = [];
  
  // Process stats
  idol.stats.forEach(stat => {
    if (!stat.modId) return;
    
    // Look up tradeId from mod database
    const tradeId = getTradeIdFromModId(stat.modId);
    if (!tradeId) return;
    
    // Add filter with value only if it's a non-zero number
    // Value of 0 means the modifier has no range (like "contains an additional strongbox")
    if (typeof stat.value === 'number' && stat.value !== 0) {
      filters.push({
        id: tradeId,
        value: { min: stat.value },
        disabled: false
      });
    } else {
      // Add filter without value for modifiers that don't have ranges or have value 0
      filters.push({ 
        id: tradeId,
        disabled: false
      });
    }
  });

  if (filters.length === 0) return "";

  // Create the query object - omitting the type field as requested
  const query: TradeQuery = {
    query: {
      stats: [{ type: "and", filters }],
      status: { option: "securable" }
    }
  };
  
  // Encode the query
  const encodedQuery = encodeURIComponent(JSON.stringify(query));
  
  // Return the URL
  return `https://www.pathofexile.com/trade/search/${league}?q=${encodedQuery}`;
};

/**
 * Checks if an idol has valid trade data
 * @param {Idol} idol - The idol object with stats
 * @returns {boolean} - Whether the idol has valid trade data
 */
export const hasValidTradeData = (idol: Idol): boolean => {
  if (!idol || !idol.stats) return false;
  
  // Check if any stat has a valid tradeId in the mod database
  return idol.stats.some(stat => {
    if (!stat.modId) return false;
    return !!getTradeIdFromModId(stat.modId);
  });
};